/**
 * permissions.ts — role 기반 권한 헬퍼.
 *
 * Backend 의 RequireRole 매트릭스와 동기화되어야 한다.
 * 매트릭스 (2026-05-21):
 *
 *   admin           : 사이트 생성/삭제, 사진 삭제, 운영 절차 글로벌 템플릿 수정,
 *                     파일 보관함 업로드/삭제, 사용자 role 변경.
 *   admin + editor  : 사이트 정보 수정, 네트워크 장비 CUD, 일정 CUD, 사건 CUD,
 *                     사건 댓글 작성/삭제, 방문 로그 CUD, 사진 업로드/수정,
 *                     사이트별 운영 절차 수정.
 *   anyone (auth)   : 모든 조회 (목록/상세/다운로드 등).
 *
 * 사용 :
 *   const { canEdit, isAdmin } = usePermissions();
 *   {canEdit && <Button>수정</Button>}
 *   {isAdmin && <Button>삭제</Button>}
 *
 * 또는 명시적 prop 전달이 필요한 경우 :
 *   const role = useRole();
 *   <RoleGate role={role} require="editor"> <SaveButton /> </RoleGate>
 */

import { getUserInfo } from './auth';

export type UserRole = 'admin' | 'editor' | 'viewer';

export const ROLE_VIEWER: UserRole = 'viewer';
export const ROLE_EDITOR: UserRole = 'editor';
export const ROLE_ADMIN: UserRole = 'admin';

const ROLE_ORDER: Record<UserRole, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
};

/**
 * 외부에서 들어온 임의 string 을 안전하게 UserRole 로 normalize.
 * 알 수 없는 값은 가장 권한이 낮은 viewer 로 fallback — fail-safe.
 */
export const normalizeRole = (raw?: string | null): UserRole => {
  if (raw === 'admin' || raw === 'editor' || raw === 'viewer') {
    return raw;
  }
  return ROLE_VIEWER;
};

/**
 * 현재 로그인된 사용자의 role.
 * 로그인 안 되어 있으면 viewer (route guard 가 별도 처리).
 */
export const getCurrentRole = (): UserRole => {
  const info = getUserInfo();
  return normalizeRole(info?.role);
};

/**
 * required 이상의 권한을 가진 role 인지.
 * (viewer < editor < admin)
 */
export const hasRoleAtLeast = (current: UserRole, required: UserRole): boolean => {
  return ROLE_ORDER[current] >= ROLE_ORDER[required];
};

/**
 * 권한별 플래그.
 *
 * Naming :
 *   - isAdmin / isEditor / isViewer : 정확히 그 role 인지
 *   - canEdit                       : editor 이상 (CUD on site data)
 *   - canAdmin                      : admin (사이트 생성/삭제, 글로벌 자산 수정)
 *
 * 함수형 사용. React 외 (axios interceptor 등) 에서도 같이 쓸 수 있다.
 */
export interface RolePermissions {
  role: UserRole;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canEdit: boolean;
  canAdmin: boolean;
}

export const getPermissions = (role: UserRole = getCurrentRole()): RolePermissions => ({
  role,
  isAdmin: role === ROLE_ADMIN,
  isEditor: role === ROLE_EDITOR,
  isViewer: role === ROLE_VIEWER,
  canEdit: hasRoleAtLeast(role, ROLE_EDITOR),
  canAdmin: hasRoleAtLeast(role, ROLE_ADMIN),
});

/**
 * Korean label — UI 표시용.
 */
export const ROLE_LABEL: Record<UserRole, string> = {
  admin: '관리자',
  editor: '편집자',
  viewer: '뷰어',
};
